import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {environment} from 'environments/environment';

import {AuthService} from './auth.service';
import {UserInfo} from './user';
import {ConfigurationService} from 'app/shared/services/configuration.service';
import {AbstractControl} from '@angular/forms';
import { PlatformFeature } from 'app/shared/objects/user';
import { MyCityInfo } from 'app/shared/objects/cities';

@Injectable()
export class UserService {
	admin: any;
	private _user: UserInfo = undefined;
	private _selectedCityId: string = undefined;
	private _myCities: MyCityInfo[] = [];

	constructor(
		private http: HttpClient,
		private authService: AuthService,
		private config: ConfigurationService) {
		this.getUserInfo();
	}

	public getUserInfo(): Promise<UserInfo> {

		if (!this.authService.isAuthenticated()) {

			return new Promise<UserInfo>((resolve, reject) => {
				resolve(new UserInfo());
			});
		}

		if (this._user !== undefined) {

			return new Promise<UserInfo>((resolve, reject) => {
				resolve(this._user);
			});

		} else {

			const header = {
				headers: new HttpHeaders()
					.set('Authorization', `Bearer ${this.authService.accessToken}`)
			}

			return this.http.get(environment.apiURL + '/dashboard/userInfo', header)
				.toPromise()
				.then(response => {
					this._user = Object.assign(new UserInfo, response);
					return this._user;
				})
				.catch(error => {
					this._user = undefined;
					return undefined;
				});
		}
	}

	isAdminPromise(): Promise<boolean> {

		return this.getUserInfo().then(response => {
			if (response != undefined && response['roles'].indexOf('ADMIN') > -1) {
				return true;
			} else {
				return false
			}
		})
			.catch(error => {
				return false;
			});
	}

	getAccountInfo(id: string) {
		return this.http.get(environment.apiURL + '/admin/account/info/' + id)
	}

	validatePasswordUppercase(control: AbstractControl) {
		const upper = new RegExp('[A-Z]+');

		if (!(upper.test(control.value)))
			return {upper: true};

		return null;
	}

	validatePasswordLowercase(control: AbstractControl) {
		const lower = new RegExp('[a-z]+');

		if (!(lower.test(control.value)))
			return {lower: true};

		return null;
	}

	validatePasswordNumber(control: AbstractControl) {
		const number = new RegExp('[0-9]+');

		if (!(number.test(control.value)))
			return {number: true};

		return null;
	}

	validateEmail(control: AbstractControl) {
		const email = new RegExp(/(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/gim);
		if (!(email.test(control.value)))
			return {email: true}

		return null
	}

	async getCountryCodes(): Promise<CountryCode[]> {

		return await this.http.get(environment.apiURL + '/users/countryCodes')
			.toPromise()
			.then(response => {
				if (response['success'])
					return response['object'] as CountryCode[];
				else {
					return null;
				}
			})
			.catch(error => {
				return null;
			});
	}

	getUserProfile(): Promise<any> {
		return this.http.get(environment.apiURL + '/users/profile')
			.toPromise()
			.then(response => response)
			.catch(error => {
				console.log('error getUserProfile: ' + JSON.stringify(error));
				return false;
			});
	}

	// Multi-city support methods
	public getMyCities(): Promise<MyCityInfo[]> {
		if (this._myCities.length > 0) {
			return Promise.resolve(this._myCities);
		}

		const header = {
			headers: new HttpHeaders()
				.set('Authorization', `Bearer ${this.authService.accessToken}`)
		};

		return this.http.get(environment.apiURL + '/restricted/myCities', header)
			.toPromise()
			.then(response => {
				if (response && response['status'] === 200 && response['object']) {
					this._myCities = response['object'] as MyCityInfo[];
				} else if (response && Array.isArray(response)) {
					this._myCities = response as MyCityInfo[];
				}
				return this._myCities;
			})
			.catch(() => {
				return [];
			});
	}

	public async getSelectedCityId(): Promise<string> {
		// Ensure user is loaded before validating city access
		await this.getUserInfo();

		// Try to load from storage if not set
		if (!this._selectedCityId) {
			const stored = localStorage.getItem('selectedCityId');
			if (stored && this.hasAccessToCity(stored)) {
				this._selectedCityId = stored;
			} else if (this._user) {
				// Default to primary city
				this._selectedCityId = this._user.propertyId || this._user.cityId;
			}
		}
		return this._selectedCityId;
	}

	public setSelectedCity(cityId: string): void {
		if (this.hasAccessToCity(cityId)) {
			this._selectedCityId = cityId;
			localStorage.setItem('selectedCityId', cityId);
		}
	}

	public hasAccessToCity(cityId: string): boolean {
		if (!this._user) return false;
		if (this.isAdmin()) return true;

		// Check propertyIds array first (multi-city), fallback to single cityId
		if (this._user.propertyIds && this._user.propertyIds.length > 0) {
			return this._user.propertyIds.includes(cityId);
		}
		return this._user.cityId === cityId;
	}

	public clearUserCache(): void {
		this._user = undefined;
		this._selectedCityId = undefined;
		this._myCities = [];
		localStorage.removeItem('selectedCityId');
	}

	getUserRoles(): string[] {
		return this._user ? this._user.roles : undefined;
	}

	isAdmin(): boolean {
		return this._user !== undefined && this._user.roles.indexOf('ADMIN') > -1;
	}

	isFree(): boolean {
		return this._user !== undefined && this._user.userPlan !== undefined && this._user.userPlan !== null && this._user.userPlan.toLowerCase() === 'free';
	}

	//TODO: Review plans features
	isPremium(): boolean {
		return true;
		//return this.isAdmin() || (this._user !== undefined && this._user.userPlan !== null && (this._user.userPlan.toLowerCase() === 'plus' || this._user.userPlan.toLowerCase() === 'plus+'));
	}


	isActive(): boolean {
		return this._user !== undefined && this._user.roles.indexOf('ACTIVE') > -1;
	}

	hasNoSubscription(): boolean {
		return this._user !== undefined && !this._user.hasPlan;
	}

	isSuspendedPayment(): boolean {
		return this._user !== undefined && this._user.suspendedPayment;
	}

	isVerificated(): boolean {
		return this._user !== undefined && this._user.roles.indexOf('PENDING_BRIGHT_VERIFICATION') == -1;
	}

	public isCityManager(cityId: string): boolean {
		return !!(
			this._user &&
			this.hasAccessToCity(cityId) &&
			(this.isPublicAdministrator() || this.canEditProfileInformation())
		);
	}

	canEditCity(cityId: string): boolean {
		return (this.isCityManager(cityId)
			&& !this.hasNoSubscription()
			&& !this.isSuspendedPayment())
			|| this.isAdmin();
	}

	canEditProfileInformation(): boolean {
		return this._user && this._user.permissions?.editProfileInformation;
	}

	canEditCompany(propertyId: string): boolean {
		return (this._user !== undefined && this._user.propertyId == propertyId && this.isSolutionProvider()) || this.isAdmin();
	}

	isCityEditor(cityId: string): boolean {
		return this._user !== undefined && this.hasAccessToCity(cityId) && this.isPublicAdministrator();
	}

	hasProfile(): boolean {
		return this.isPublicAdministrator() || this.isCitizen() || this.isSolutionProvider();
	}

	getProfile(): string {
		// if (!this._user)
		//   this.getUserInfo();

		return this.config.userProfileList[this.getProfileTranslationKey()].name;
	}

	getProfileKey(): string {
		// if (!this._user)
		//   this.getUserInfo();

		if (this.isPublicAdministrator())
			return 'PUBLIC_ADMINISTRATOR';
		else if (this.isSolutionProvider())
			return 'SOLUTION_PROVIDER';
		else
			return 'CITIZEN';
	}

	getProfileTranslationKey(): string {
		// if (!this._user)
		//   this.getUserInfo();

		if (this.isPublicAdministrator())
			return 'create-account/' + 'PUBLIC_ADMINISTRATOR';
		else if (this.isSolutionProvider())
			return 'create-account/' + 'SOLUTION_PROVIDER';
		else
			return 'create-account/' + 'CITIZEN';
	}

	isPublicAdministrator(): boolean {
		return this._user != undefined && (this._user.roles.indexOf('PUBLIC_ADMINISTRATOR') > -1);
	}

	isCitizen(): boolean {
		return this._user != undefined && (this._user.roles.indexOf('CITIZEN') > -1);
	}

	isSolutionProvider(): boolean {
		return this._user != undefined && (this._user.roles.indexOf('SOLUTION_PROVIDER') > -1);
	}

	isGuest(): boolean {
		return this._user != undefined && (this._user.roles.indexOf('GUEST') > -1);
	}

	public userPlanHasPlatformFeature(platformFeature: PlatformFeature): boolean {
		return this._user != undefined && this._user.platformFeatures != undefined && (this._user.platformFeatures.indexOf(platformFeature) > -1);
	}

	public userCanAccessPlatformFeatureByProviderId(platformFeature: PlatformFeature, providerId: string): boolean {
		if (this.authService.isAuthenticated()) {
			if (this.isAdmin()) return true;
			
			if (this.canEditCompany(providerId) &&
				this.isVerificated() &&
				this.userPlanHasPlatformFeature(platformFeature))
				return true;
		}

		return false;
	}

	public userCanAccessOpportunitiesMap(providerId: string): boolean {
		return this.userCanAccessPlatformFeatureByProviderId(PlatformFeature.OPPORTUNITIES_MAP, providerId);
	}

	public userCanAccessPlatformFeatureByCityId(platformFeature: PlatformFeature, cityId: string): boolean {
		if (this.authService.isAuthenticated()) {
			if (this.isAdmin()) return true;
			
			if (this.isCityManager(cityId) &&
				this.isVerificated() &&
				this.userPlanHasPlatformFeature(platformFeature))
				return true;
		}

		return false;
	}

	public userCanAccessCityDiagnosis(cityId: string): boolean {
		return this.userCanAccessPlatformFeatureByCityId(PlatformFeature.CITY_DIAGNOSIS, cityId);
	}

	public userCanAccessCityCertification(cityId: string) {
		return this.userCanAccessPlatformFeatureByCityId(PlatformFeature.CITY_CERTIFICATION, cityId);
	}

	public userCanAccessSolutionsRoadmap(cityId: string) {
		return this.userCanAccessPlatformFeatureByCityId(PlatformFeature.SOLUTIONS_ROADMAP, cityId);
	}

}

export interface CountryCode {
	id: string;
	area: string;
	country: string;
	code: string;
}
